import { useTranslation } from "react-i18next";
import UserProfilePictureAvatar from "../avatar/UserProfilePictureAvatar";
import Modal from "./ModalWithFooter";
import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckIcon,
  ClockIcon,
  ExclamationIcon,
} from "@heroicons/react/outline";
import { RequestsType } from "../../api/@types/globalTypes";
import moment from "moment";
import ApiDateParser from "../../helpers/parsers/apiDateParser";
import { makeApiRequest } from "../../helpers";
import { ApiResponse } from "../../api/apiResponse";
import {
  indexCancellationRequestWithId,
  indexRequestWithId,
} from "../../api/endpoints/supervisors/sessions";
import { useEffect, useState } from "react";
import RequestsModel from "../../api/models/RequestsModel";
type Props = {
  id: number | string;
  type: string;
  onClose?: () => void;
  cancel: boolean;
  setIsOpen: () => void;
  open: boolean;
};

export default function RequestInfoModal({
  id,
  onClose,
  open,
  cancel,
  setIsOpen,
  type,
}: Props) {
  const LocalBasePath = "page.supervisor.tutor";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [request, setRequest] = useState<RequestsType>();
  const days: any = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  const getRequestData = (id: string | number) => {
    if (type === "cancellation") {
      makeApiRequest(
        () => indexCancellationRequestWithId(id),
        (response: ApiResponse) => {
          if (response.isSuccess) {
            const { data } = response;

            setRequest(data);
          }
        }
      );
    } else {
      makeApiRequest(
        () => indexRequestWithId(id),
        (response: ApiResponse) => {
          if (response.isSuccess) {
            const { data } = response;
            setRequest(data);
          }
        }
      );
    }
  };
  useEffect(() => {
    if (!!id && open) getRequestData(id);
  }, [id, open]);

  return (
    <Modal className="max-w-[560px]" open={open} setOpen={onClose}>
      <Modal.Body
        className={"border-b border-gray-200 pb-4"}
        setOpen={onClose}
        title={
          t(request?.object_type ?? "") +
          " " +
          moment(request?.created_at).format("DD/MM/YYYY")
        }
      >
        <div className="bg-white h-full   ">
          <div className="h-full overflow-y-auto overflow-x-hidden  md:mx-0">
            <div className="w-full flex flex-col">
              <div className="w-full flex  items-center justify-end">
                {request?.status === "rejected" ? (
                  <span className="border-red-600 gap-1 border text-red-600 flex justify-center items-center flex-nowrap   text-xs font-medium py-1 px-2.5  rounded-xl">
                    <ExclamationIcon className="w-4 h-4 text-red-600" />
                    <p>{t("requestCanceled")}</p>
                  </span>
                ) : request?.status === "pending" ? (
                  <span className="border-gray-400 gap-1 border text-gray-400 flex justify-center items-center flex-nowrap   text-xs font-medium  py-1 px-2  rounded-xl">
                    <ClockIcon className="w-4 h-4 text-gray-400" />
                    <p>{t("requestPending")}</p>
                  </span>
                ) : (
                  <span className="border-green-500 gap-1 border text-green-500 flex justify-center items-center flex-nowrap   text-xs font-medium py-1 px-2.5  rounded-xl">
                    <CheckIcon className="w-4 h-4 text-green-600" />
                    <p>{t("requestAccepted")}</p>
                  </span>
                )}
              </div>
              <div className="w-full flex   gap-5">
                <div className="flex  items-start gap-3  mb-6">
                  <UserProfilePictureAvatar
                    profilePictureUrl={request?.sender?.profile_picture_url}
                    className="w-14 h-14 flex-shrink-0"
                    alt={request?.sender?.first_name}
                  />
                  <div className="flex flex-col gap-1">
                    <p className="text-gray-600 text-xl leading-6 font-medium">
                      {request?.sender?.first_name +
                        " " +
                        request?.sender?.last_name}
                    </p>
                    <p className="text-sm leading-5 text-gray-400 ">
                      {t(request?.sender?.object_type ?? "")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center ">
                  <ArrowLeftIcon className="text-gray-600   ltr:rotate-180 w-6 h-6" />
                </div>
                <div className="flex  items-start gap-3  mb-6">
                  <UserProfilePictureAvatar
                    profilePictureUrl={request?.receiver?.profile_picture_url}
                    className="w-14 h-14 flex-shrink-0"
                    alt={request?.receiver?.first_name}
                  />
                  <div className="flex flex-col gap-1">
                    <p className="text-gray-600 text-xl leading-6 font-medium">
                      {request?.receiver?.first_name +
                        " " +
                        request?.receiver?.last_name}
                    </p>
                    <p className="text-sm leading-5 text-gray-400 ">
                      {t(request?.receiver?.object_type ?? "")}
                    </p>
                  </div>
                </div>
              </div>
              {request?.object_type === "Cancellation" && (
                <>
                  <div className="w-full   mb-2   flex flex-col items-start  py-4 gap-2">
                    <div>
                      <p className="text-sm  font-medium text-gray-600">
                        {t("sessionNum", {
                          num: request?.session_number ?? "",
                        })}
                      </p>
                    </div>
                    <div className="w-full flex gap-4 items-center">
                      <div className="text-gray-400 text-sm font-medium flex gap-1.5 items-center">
                        <CalendarIcon className="w-5 h-5 text-gray-400" />
                        <p> {moment(request?.date).format("dddd M/D")}</p>
                      </div>
                      <div className="text-gray-400 text-sm font-medium flex gap-1.5 items-center">
                        <ClockIcon className="w-5 h-5 text-gray-400" />
                        <p>
                          {ApiDateParser.parse(request?.start_time).format(
                            "hh:mm A"
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="w-full   mb-4   flex flex-col items-start  py-4">
                    <div>
                      <p className="text-sm  font-medium text-gray-600">
                        {t("reason")}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">
                        {request?.cancellation_reason}
                      </p>
                    </div>
                  </div>
                </>
              )}
              {request?.object_type === "ScheduleRequest" && (
                <div className="flex flex-col gap-8">
                  <div className="w-full      flex  flex-col items-start  pt-4 ">
                    <div>
                      <p className="text-sm  leading-8 font-medium text-gray-600">
                        {t("oldSchedules")}
                      </p>
                    </div>

                    <div className="w-full   mb-4    flex flex-col items-start  gap-2">
                      {!!request?.recitation_schedule_slots?.schedule_slots
                        ?.length &&
                        request?.recitation_schedule_slots?.schedule_slots.map(
                          (slot) => (
                            <div className="flex flex-col gap-1 w-80">
                              <p className="text-sm  font-medium text-gray-500">
                                {moment()
                                  .day(days?.[slot?.day_of_the_week] ?? 0)
                                  .format("dddd")}
                              </p>
                              <div>
                                <p className="text-sm font-medium text-brandblue-600">
                                  {ApiDateParser.parse(slot?.start_time).format(
                                    "hh:mm A"
                                  )}
                                  {}
                                  {"  -  "}
                                  {ApiDateParser.parse(slot?.end_time).format(
                                    "hh:mm A"
                                  )}
                                </p>
                              </div>
                            </div>
                          )
                        )}
                    </div>
                  </div>
                  <div className="w-full      flex  flex-col items-start  ">
                    <div>
                      <p className="text-sm  leading-8 font-medium text-gray-600">
                        {t("recommendedSchedules")}
                      </p>
                    </div>

                    <div className="w-full   mb-4    flex flex-col items-start  gap-2">
                      {!!request?.schedule?.schedule_slots?.length &&
                        request?.schedule?.schedule_slots.map((slot) => (
                          <div className="flex flex-col gap-1 w-80">
                            <p className="text-sm  font-medium text-gray-500">
                              {moment()
                                .day(days?.[slot?.day_of_the_week] ?? 0)
                                .format("dddd")}
                            </p>
                            <div>
                              <p className="text-sm font-medium text-brandblue-600">
                                {ApiDateParser.parse(slot?.start_time).format(
                                  "hh:mm A"
                                )}
                                {}
                                {"  -  "}
                                {ApiDateParser.parse(slot?.end_time).format(
                                  "hh:mm A"
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
              {request?.object_type === "ProposedSchedule" && (
                <div className="flex flex-col gap-8">
                  <div className="w-full      flex  flex-col items-start  ">
                    <div>
                      <p className="text-sm  leading-8 font-medium text-gray-600">
                        {t("recommendedSchedules")}
                      </p>
                    </div>

                    <div className="w-full   mb-4    flex flex-col items-start  gap-2">
                      {!!request?.schedule?.schedule_slots?.length &&
                        request?.schedule?.schedule_slots.map((slot) => (
                          <div className="flex flex-col gap-1 w-80">
                            <p className="text-sm  font-medium text-gray-500">
                              {moment()
                                .day(days?.[slot?.day_of_the_week] ?? 0)
                                .format("dddd")}
                            </p>
                            <div>
                              <p className="text-sm font-medium text-brandblue-600">
                                {ApiDateParser.parse(slot?.start_time).format(
                                  "hh:mm A"
                                )}
                                {}
                                {"  -  "}
                                {ApiDateParser.parse(slot?.end_time).format(
                                  "hh:mm A"
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="w-full flex  justify-end">
          <button
            type="button"
            className=" inline-flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 bg-brandblue-600 focus:ring-offset-2 border-brandblue-600  text-white hover:text-brandblue-600  hover:bg-white disabled:opacity-80 disabled:hover:bg-brandblue-600 focus:ring-brandblue-500"
            onClick={() => !!setIsOpen && setIsOpen()}
          >
            <div className="flex gap-2">{t("close")}</div>
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
